/**
 * Custom Rugs Handler
 * Handles sequential accordion opening, size dimension calculation, and all custom rug functionality
 * Matches TypeScript custom-rugs.directive.ts behavior
 */

class CustomRugsHandler {
  constructor(container) {
    this.container = container;
    this.cylindoSelects = [];
    this.chosenShape = null;
    this.chosenArea = 0;
    this.chosenPerimeter = 0;
    
    // Get price per sq ft from container attribute (matches TypeScript line 14-15)
    // Note: Shopify product.price is in cents, so we get it as a number in cents
    const pricePerSqFtAttr = container.getAttribute('price-per-sq-ft');
    const cleanPrice = pricePerSqFtAttr ? pricePerSqFtAttr.replace(/[,$]/g, '') : '0';
    this.pricePerSqFt = parseFloat(cleanPrice) || 0;
    
    // Load flowProducts from JSON (matches TypeScript line 126)
    // This contains serging_product (finishing) and rug_pad_product
    this.flowProducts = this.loadFlowProducts();
    
    this.priceDisplay = document.querySelector('.product-metas__price .price-value__lowest');
    
    // Get add to cart button (matches TypeScript line 33-34)
    this.addToCartBtn = document.querySelector('button[type="submit"][add-to-cart][add-to-cart-flow]') ||
                        document.querySelector('[data-add-to-cart]') ||
                        document.querySelector('.product-form__cart-submit');
    
    // Store current variant for button updates
    this.currentVariant = null;
    
    // Expose instance globally for add-to-cart-handler to access
    window.customRugsHandler = this;
    
    this.init();
  }

  /**
   * Load flowProducts from CustomRugsJSON script
   * Matches TypeScript data loading (line 67, 77, 126)
   * TypeScript: this.json = JSON.parse(document.getElementById('CustomRugsJSON').textContent)
   * Then: this.flowProducts = [this.data.serging_product, this.data.rug_pad_product]
   */
  loadFlowProducts() {
    try {
      // Get flow handle to find the right flow data
      const handle = this.container.getAttribute('custom-rugs');
      if (!handle) {
        console.warn('custom-rugs handle not found');
        return [];
      }
      
      // Load CustomRugsJSON (contains flow data with serging_product and rug_pad_product)
      // Matches TypeScript line 67
      const jsonScript = document.getElementById('CustomRugsJSON');
      if (!jsonScript) {
        // Silent fail - CustomRugsJSON only exists on collection/product pages with custom rugs
        return [];
      }
      
      const flows = JSON.parse(jsonScript.textContent);
      
      // Find the flow matching the handle (matches TypeScript line 77-78)
      const flowData = flows.find(flow => flow.handle === handle);
      
      if (!flowData) {
        console.warn(`Flow data not found for handle: ${handle}`);
        return [];
      }
      
      // Get serging_product and rug_pad_product (matches TypeScript line 126)
      const flowProducts = [];
      if (flowData.serging_product) {
        flowProducts.push(flowData.serging_product);
      }
      if (flowData.rug_pad_product) {
        flowProducts.push(flowData.rug_pad_product);
      }
      
      return flowProducts;
    } catch (e) {
      console.error('Error loading flowProducts:', e);
      return [];
    }
  }

  init() {
    // Get all cylindo variant selectors
    const selectors = this.container.querySelectorAll('[cylindo-variant-selector]');
    selectors.forEach(selector => {
      const toggle = selector.cylindoToggleInstance;
      if (toggle) {
        this.cylindoSelects.push({
          element: selector,
          toggle: toggle,
          optionName: selector.getAttribute('cylindo-variant-selector')
        });
      }
    });

    // Sort by step number (Size=1, Color=2, Shape=3, etc.)
    this.cylindoSelects.sort((a, b) => {
      const stepA = parseInt(a.element.querySelector('.cylindoVariant__info--step')?.textContent || '999');
      const stepB = parseInt(b.element.querySelector('.cylindoVariant__info--step')?.textContent || '999');
      return stepA - stepB;
    });

    // On page load: Size is step 1, enabled and open; Color/Shape enabled but closed
    this.cylindoSelects.forEach((select) => {
      const optionName = select.optionName.toLowerCase();

      if (optionName === 'size') {
        select.toggle.enable();
        if (!select.toggle.isOpen) {
          select.toggle.open();
        }
      } else if (!['color', 'shape'].includes(optionName)) {
        select.toggle.disable();
        if (select.toggle && select.toggle.isOpen) {
          select.toggle.close();
        }
      } else {
        select.toggle.enable();
        if (select.toggle && select.toggle.isOpen) {
          select.toggle.close();
        }
      }
    });

    // Auto-select first available Color variant on page load (matches TypeScript line 128-131)
    // TypeScript uses rugProductVariants[0] if no color is checked
    const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
    if (colorSelector) {
      // Check if color is already selected
      const checkedColor = colorSelector.element.querySelector('input[type="radio"]:checked');
      
      if (!checkedColor) {
        // If no color selected, select the first available color option
        const firstColorInput = colorSelector.element.querySelector('input[type="radio"]');
        if (firstColorInput) {
          firstColorInput.checked = true;
          // Update display value silently (don't trigger events)
          if (colorSelector.toggle && colorSelector.toggle.selectedLabel) {
            const label = firstColorInput.closest('label');
            if (label) {
              const labelTextElement = label.querySelector('.custom-rug-option__label');
              if (labelTextElement) {
                colorSelector.toggle.selectedLabel.textContent = labelTextElement.textContent.trim();
              } else if (firstColorInput.value) {
                colorSelector.toggle.selectedLabel.textContent = firstColorInput.value;
              }
            }
          }
          
          // Get variant and update add to cart button (matches TypeScript line 131)
          const variant = this.getVariantFromColorValue(firstColorInput.value);
          if (variant) {
            this.currentVariant = variant;
            
            // Update add to cart button attributes (matches TypeScript line 186)
            if (this.addToCartBtn) {
              this.addToCartBtn.setAttribute('add-to-cart', variant.id.toString());
              this.addToCartBtn.disabled = !variant.available || !this.hasShapeAndColor();
              
              // Set flow handle if not already set
              if (!this.addToCartBtn.getAttribute('custom-rug-flow-handle')) {
                const flowHandle = this.container.getAttribute('custom-rugs');
                if (flowHandle) {
                  this.addToCartBtn.setAttribute('custom-rug-flow-handle', flowHandle);
                }
              }
            }
          }
          
          // Update URL and gallery with variant ID on page load (user requirement)
          this.updateURLFromColorSelection(firstColorInput.value);
          // Dispatch variant:change event to update gallery
          this.dispatchVariantChangeEvent(firstColorInput.value);
        }
      } else {
        // Color already selected - update display value and initialize variant
        // Update display value to show selected color name in span
        if (colorSelector.toggle && colorSelector.toggle.selectedLabel) {
          const label = checkedColor.closest('label');
          if (label) {
            const labelTextElement = label.querySelector('.custom-rug-option__label');
            if (labelTextElement) {
              colorSelector.toggle.selectedLabel.textContent = labelTextElement.textContent.trim();
            } else if (checkedColor.value) {
              colorSelector.toggle.selectedLabel.textContent = checkedColor.value;
            }
          }
        }
        
        const variant = this.getVariantFromColorValue(checkedColor.value);
        if (variant) {
          this.currentVariant = variant;
          
          if (this.addToCartBtn) {
            this.addToCartBtn.setAttribute('add-to-cart', variant.id.toString());
            this.addToCartBtn.disabled = !variant.available || !this.hasShapeAndColor();
            
            // Set flow handle if not already set
            if (!this.addToCartBtn.getAttribute('custom-rug-flow-handle')) {
              const flowHandle = this.container.getAttribute('custom-rugs');
              if (flowHandle) {
                this.addToCartBtn.setAttribute('custom-rug-flow-handle', flowHandle);
              }
            }
          }
        }
        
        // Update URL and gallery with current variant
        this.updateURLFromColorSelection(checkedColor.value);
        // Dispatch variant:change event to update gallery
        this.dispatchVariantChangeEvent(checkedColor.value);
      }
      
      // DO NOT open Color accordion - keep all accordions closed by default (client requirement)
      // Removed: colorSelector.toggle.open()
    }

    this.bindEvents();
    this.initializeSizeDimensions();
    // Activate pre-filled size dimensions and downstream option defaults on load
    this.applySizeDefaults();
  }

    bindEvents() {
    // Listen for radio button clicks (matches TypeScript line 141)
    // This handles closing current accordion when option is clicked and opening next
    this.container.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('click', (e) => {
        this.onOptionClick(e);
      });
    });
    
    // Listen for variant changes (after selection is made)
    this.container.addEventListener('change', (e) => {
      this.handleVariantChange(e);
      // Update price when any option changes (finishing, rug pad, etc.)
      if (this.chosenArea > 0 || this.chosenPerimeter > 0) {
        this.updatePrice();
      }
    });

    // Listen for size dimension input changes
    const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
    if (sizeSelector) {
      const sizeInputs = sizeSelector.element.querySelectorAll('input[type="number"]');
      sizeInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          e.stopPropagation(); // Prevent event from bubbling
          this.handleSizeChange();
        });
        input.addEventListener('input', (e) => {
          e.stopPropagation(); // Prevent event from bubbling
          this.handleSizeChange();
        });
      });
      
      // Prevent + / - buttons from closing the accordion
      // Stop event propagation on quantity selector buttons
      const quantityButtons = sizeSelector.element.querySelectorAll('[quantity-selector-increase], [quantity-selector-decrease]');
      quantityButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent click from bubbling to accordion toggle
        });
      });
    }

    // Don't update price on initial load - wait for user to select options
  }

  /**
   * Handle option click - closes current accordion and opens next one if applicable
   * Matches TypeScript onOptionClick() line 537-552
   */
  onOptionClick(event) {
    event.stopPropagation();
    const target = event.target;
    if (!target || target.type !== 'radio') return;
    
    // Only handle if option is being checked (not unchecked)
    if (!target.checked) return;
    
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;
    
    const selector = this.cylindoSelects.find(s => 
      fieldset.contains(s.element)
    );
    if (!selector) return;
    
    const fieldsetIdx = this.cylindoSelects.indexOf(selector);
    const optionName = selector.optionName.toLowerCase();
    const hasShapeAndColor = this.hasShapeAndColor();

    // Don't advance until both Color and Shape are selected
    if (['color', 'shape'].includes(optionName) && !hasShapeAndColor) {
      return;
    }

    // Close current and open next (matches TypeScript line 550 - goToNextStep)
    this.goToNextStep(fieldsetIdx);
  }
  
  /**
   * Close current accordion and open next one that needs selection
   * Matches TypeScript goToNextStep() line 586-595
   */
  goToNextStep(index) {
    const current = this.cylindoSelects[index];
    // Find next selector that has no selection and empty display value
    const next = this.cylindoSelects.slice(index + 1).find(s => {
      const hasSelection = !!s.element.querySelector('input:checked');
      const hasDisplayValue = s.toggle.selectedLabel && s.toggle.selectedLabel.textContent.trim().length > 0;
      return !hasSelection && !hasDisplayValue;
    });
    
    // Close current accordion (Size stays open unless user toggles it directly)
    if (current && current.toggle.isOpen && current.optionName.toLowerCase() !== 'size') {
      current.toggle.close();
    }
    
    // Enable and open next accordion (only if there's a next step to select)
    if (next) {
      next.toggle.enable();
      next.toggle.open();
    }
    // If no next step found, all are selected - keep current closed (don't auto-open anything)
  }

  handleVariantChange(event) {
    const target = event.target;
    if (!target || target.type !== 'radio') return;

    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    const selector = this.cylindoSelects.find(s => 
      fieldset.contains(s.element)
    );
    if (!selector) return;

    const optionName = selector.optionName.toLowerCase();

    // When Color is selected
    // Matches TypeScript custom-rugs.directive.ts line 183-206
    if (optionName === 'color') {
      // Get variant from CustomRugsProducts (matches TypeScript line 184)
      const variant = this.getVariantFromColorValue(target.value);
      if (variant) {
        this.currentVariant = variant;
        
        // Update add to cart button with variant ID (matches TypeScript line 186)
        if (this.addToCartBtn) {
          this.addToCartBtn.setAttribute('add-to-cart', variant.id.toString());
          // Update button disabled state (matches TypeScript line 187)
          this.addToCartBtn.disabled = !variant.available || !this.hasShapeAndColor();
          
          // Set flow handle if not already set (matches live site button attributes)
          if (!this.addToCartBtn.getAttribute('custom-rug-flow-handle')) {
            const flowHandle = this.container.getAttribute('custom-rugs');
            if (flowHandle) {
              this.addToCartBtn.setAttribute('custom-rug-flow-handle', flowHandle);
            }
          }
          
          // Update button text (matches TypeScript line 188)
          // But only if we don't have dimensions yet (price will update later)
          if (!variant.available || !this.hasShapeAndColor()) {
            const addToCartText = variant.available ? 'Add to cart' : 'Sold out';
            this.addToCartBtn.textContent = addToCartText;
          }
        }
        
        // Update upsell/sample tile (matches TypeScript line 190-191)
        this.updateUpsell(variant);
      }
      
      // Update URL and gallery with variant ID when color changes (user requirement)
      if (target.value) {
        this.updateURLFromColorSelection(target.value);
        // Dispatch variant:change event to update gallery
        this.dispatchVariantChangeEvent(target.value);
      }
      
      // Update display value and other color-specific logic
      // Note: Closing color and opening shape is handled by onOptionClick
      
      // If both Color and Shape are selected, enable Size (but don't auto-open)
      // Opening is handled by onOptionClick -> goToNextStep
      // Matches TypeScript line 193-204 (but TypeScript opens via handleFieldsetChange, we use goToNextStep)
      if (this.hasShapeAndColor()) {
        const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
        if (sizeSelector) {
          sizeSelector.toggle.enable();
          // Clear size display initially (matches TypeScript line 195)
          if (sizeSelector.toggle.selectedLabel) {
            sizeSelector.toggle.selectedLabel.textContent = '';
          }
          // Don't auto-open - let goToNextStep handle it when user clicks
        }
      }
      return;
    }

    // When Shape is selected
    // Matches TypeScript custom-rugs.directive.ts line 209-221
    if (optionName === 'shape') {
      const hasChanged = this.chosenShape !== target.value;
      this.chosenShape = target.value;
      this.setSizeFields(this.chosenShape);
      
      // Update prices after setting size fields (matches TypeScript behavior)
      if (this.chosenArea > 0 || this.chosenPerimeter > 0) {
        this.updateFinishingPrices();
        this.updateRugPadPrices();
      }

      // Note: Opening Size is handled by onOptionClick when both Color and Shape are selected
      // This just handles the shape change logic
      return;
    }

    // Handle Finishing and Rug Pad (matches TypeScript line 229-235 EXACTLY)
    if (["matching rug pad", "finishing"].includes(optionName)) {
      const value = selector.element.querySelector('input[type="radio"]:checked')?.value;
      if (value) {
        const variant = this.flowProducts.flatMap(p => p.variants || []).find(v => v.id === +value);
        if (variant && selector.toggle) {
          // Matches TypeScript line 233: current.updateDisplayValue(new Event('change'), variant.title)
          selector.toggle.updateDisplayValue(new Event('change'), variant.title);
        }
      }
      
      // Matches TypeScript line 237
      this.updatePrice();
      this.validate();
      return;
    }

    // Matches TypeScript line 237
    this.updatePrice();
    this.validate();
  }

  hasShapeAndColor() {
    const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
    const shapeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'shape');
    
    const hasColor = colorSelector?.element.querySelector('input[type="radio"]:checked');
    const hasShape = shapeSelector?.element.querySelector('input[type="radio"]:checked');
    
    return !!hasColor && !!hasShape;
  }

  setSizeFields(shape) {
    const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
    if (!sizeSelector) return;

    const sizeFieldset = sizeSelector.element.closest('fieldset');
    if (!sizeFieldset) return;

    // Set active/inactive based on shape
    const dimensionContainers = sizeFieldset.querySelectorAll('.custom-rug-option__dimension');
    dimensionContainers.forEach(container => {
      const shapeClass = `custom-rug-option__dimension--${shape}`;
      if (container.classList.contains(shapeClass)) {
        container.setAttribute('size-fields', 'active');
        container.querySelectorAll('input').forEach(input => {
          input.disabled = false;
          input.removeAttribute('tabindex');
          if (input.type === 'number') {
            input.tabIndex = 0;
          }
        });
      } else {
        container.setAttribute('size-fields', 'idle');
        container.querySelectorAll('input').forEach(input => {
          input.disabled = true;
          input.tabIndex = -1;
        });
      }
    });
  }

  initializeSizeDimensions() {
    // Auto-select Rectangle shape if no shape is selected (client requirement)
    const shapeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'shape');
    if (shapeSelector) {
      const checkedShape = shapeSelector.element.querySelector('input[type="radio"]:checked');
      
      if (!checkedShape) {
        // No shape selected - auto-select Rectangle
        const rectangleInput = shapeSelector.element.querySelector('input[type="radio"][value="rectangle"]') ||
                               Array.from(shapeSelector.element.querySelectorAll('input[type="radio"]')).find(input => {
                                 const label = input.closest('label');
                                 const labelText = label?.querySelector('.custom-rug-option__label')?.textContent.trim().toLowerCase();
                                 return labelText === 'rectangle';
                               });
        
        if (rectangleInput) {
          rectangleInput.checked = true;
          this.chosenShape = rectangleInput.value || 'rectangle';
          this.setSizeFields(this.chosenShape);
          
          // Update display value to show "Rectangle" in the span
          if (shapeSelector.toggle && shapeSelector.toggle.selectedLabel) {
            const label = rectangleInput.closest('label');
            if (label) {
              const labelTextElement = label.querySelector('.custom-rug-option__label');
              if (labelTextElement) {
                shapeSelector.toggle.selectedLabel.textContent = labelTextElement.textContent.trim();
              } else {
                shapeSelector.toggle.selectedLabel.textContent = 'Rectangle';
              }
            }
          }
        }
      } else {
        // Shape already selected - initialize size fields
        this.chosenShape = checkedShape.value;
        this.setSizeFields(this.chosenShape);
      }
      
      // Enable Size accordion if both Color and Shape are selected
      if (this.hasShapeAndColor()) {
        const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
        if (sizeSelector) {
          sizeSelector.toggle.enable();
        }
      }
    }
  }

  /**
   * Apply default size values on load, auto-select downstream options, and open Size accordion
   */
  applySizeDefaults() {
    this.handleSizeChange();

    const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
    if (sizeSelector) {
      sizeSelector.toggle.enable();
      if (!sizeSelector.toggle.isOpen) {
        sizeSelector.toggle.open();
      }
    }
  }

  getSizeInputValue(input) {
    if (input.value === '') return 0;
    const value = input.valueAsNumber;
    return isNaN(value) ? null : value;
  }

  handleSizeChange() {
    if (!this.chosenShape) return;

    const sizeSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'size');
    if (!sizeSelector || !sizeSelector.toggle.selectedLabel) return;

    const dimensionContainer = this.container.querySelector(
      `.custom-rug-option__dimension--${this.chosenShape}[size-fields="active"]`
    );
    
    if (!dimensionContainer) return;

    const inputs = dimensionContainer.querySelectorAll('input[type="number"]');
    if (inputs.length === 0) return;

    // Empty inch fields default to 0 so pre-filled feet-only dimensions are valid on load
    const allFilled = Array.from(inputs).every(input => {
      const value = this.getSizeInputValue(input);
      return value !== null && value >= 0;
    });

    if (!allFilled) {
      // Clear display if not all filled
      sizeSelector.toggle.selectedLabel.textContent = '';
      this.chosenArea = 0;
      this.chosenPerimeter = 0;
      // Update price without dimensions
      this.updatePrice();
      this.validate();
      return;
    }

    // Get values array for calculations (matches TypeScript format)
    const values = Array.from(inputs).map(input => ({
      [input.name]: input.value === '' ? '0' : input.value
    }));

    // Calculate area and perimeter (matches TypeScript getArea/getPerimeter line 453-505)
    this.chosenArea = this.getArea(values);
    this.chosenPerimeter = this.getPerimeter(values);

    // Calculate dimension string
    const dimString = this.getDimensionString(dimensionContainer, this.chosenShape);
    
    // Update selected value display
    sizeSelector.toggle.updateDisplayValue({ target: null }, dimString);
    
    // Update finishing and rug pad prices (matches TypeScript line 258-259)
    this.updateFinishingPrices();
    this.updateRugPadPrices();
    
    // Check if size is valid (inventory check) - matches TypeScript line 198-203
    const isAvailable = !this.currentVariant ||
      this.chosenArea <= (this.currentVariant.inventory_quantity || Infinity);

    if (this.addToCartBtn) {
      this.addToCartBtn.setAttribute('has-valid-size', isAvailable ? 'true' : 'false');
    }

    if (dimensionContainer) {
      dimensionContainer.setAttribute('size-fields', isAvailable ? 'active' : 'error');

      const errorMsg = dimensionContainer.querySelector('.error-msg');
      if (errorMsg && !isAvailable) {
        errorMsg.textContent = 'Custom rug size exceeds available inventory';
      } else if (errorMsg) {
        errorMsg.textContent = '';
      }
    }

    // Enable and auto-select Finishing and Rug Pad before validating add-to-cart state
    const finishingSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'finishing');
    const rugPadSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'matching rug pad');

    if (finishingSelector) {
      finishingSelector.toggle.enable();
      if (!finishingSelector.element.querySelector('input[type="radio"]:checked')) {
        this.autoSelectFirstOption(finishingSelector);
      }
    }
    if (rugPadSelector) {
      rugPadSelector.toggle.enable();
      if (!rugPadSelector.element.querySelector('input[type="radio"]:checked')) {
        this.autoSelectFirstOption(rugPadSelector);
      }
    }

    // Update price display with dimensions (including finishing/rug pad selections)
    this.updatePrice();

    // Validate and update button state after all defaults are applied
    this.validate();
  }

  /**
   * Auto-select the first radio option and update the accordion display label
   */
  autoSelectFirstOption(selector) {
    if (!selector?.element) return;

    const firstInput = selector.element.querySelector('input[type="radio"]');
    if (!firstInput) return;

    firstInput.checked = true;

    const optionName = selector.optionName.toLowerCase();
    if (['finishing', 'matching rug pad'].includes(optionName)) {
      const variant = this.flowProducts
        .flatMap((p) => p.variants || [])
        .find((v) => v.id === +firstInput.value);
      if (variant && selector.toggle) {
        selector.toggle.updateDisplayValue(new Event('change'), variant.title);
      }
    } else if (selector.toggle) {
      selector.toggle.updateDisplayValue({ target: firstInput });
    }
  }

  /**
   * Get dimension string from container (for display in size selector)
   */
  getDimensionString(container, shape) {
    const inputs = container.querySelectorAll('input[type="number"]');
    const values = Array.from(inputs).map(input => ({ [input.name]: input.value }));
    return this.getDimensionStringFromValues(values, shape);
  }

  /**
   * Get dimension string from values array
   * Matches TypeScript getDimensionString() line 279-305
   */
  getDimensionStringFromValues(values, shape) {
    if (shape === 'rectangle') {
      const widthFeet = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      const lengthFeet = +values[2]['properties[length-feet]'] || 0;
      const lengthInch = +values[3]['properties[length-inches]'] || 0;
      
      // Format: "5' x 5'" or "5' 6" x 7' 8"" (matches TypeScript line 289-290)
      const width = `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''}`;
      const length = `${lengthFeet}'${lengthInch > 0 ? `${lengthInch}"` : ''}`;
      
      return `${width} x ${length}`;
    } else if (['octagon', 'round'].includes(shape)) {
      const diameterFeet = +values[0]['properties[diameter-feet]'] || 0;
      const diameterInch = +values[1]['properties[diameter-inches]'] || 0;
      
      return `${diameterFeet}'${diameterInch > 0 ? `${diameterInch}"` : ''}`;
    } else {
      // square
      const widthFeet = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      
      return `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''}`;
    }
  }

  /**
   * Calculate area in square feet (matches TypeScript getArea() line 453-475)
   */
  getArea(values) {
    if (!this.chosenShape || this.chosenShape === 'rectangle') {
      const width = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      const length = +values[2]['properties[length-feet]'] || 0;
      const lengthInch = +values[3]['properties[length-inches]'] || 0;
      const totalLength = length + (lengthInch / 12);
      const totalWidth = width + (widthInch / 12);
      return Math.ceil(totalWidth * totalLength);
    } else if (['round', 'octagon'].includes(this.chosenShape)) {
      const diameter = +values[0]['properties[diameter-feet]'] || 0;
      const diameterInch = +values[1]['properties[diameter-inches]'] || 0;
      const total = diameter + (diameterInch / 12);
      return Math.ceil(total * total);
    } else if (this.chosenShape === 'square') {
      const width = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      const total = width + (widthInch / 12);
      return Math.ceil(total * total);
    }
    return 0;
  }

  /**
   * Calculate perimeter in feet (matches TypeScript getPerimeter() line 477-505)
   */
  getPerimeter(values) {
    if (this.chosenShape === 'rectangle') {
      const widthFeet = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      const lengthFeet = +values[2]['properties[length-feet]'] || 0;
      const lengthInch = +values[3]['properties[length-inches]'] || 0;
      const width = widthFeet + (widthInch / 12);
      const length = lengthFeet + (lengthInch / 12);
      return Math.ceil(width * 2 + length * 2);
    } else if (['octagon', 'round'].includes(this.chosenShape)) {
      const diameterFeet = +values[0]['properties[diameter-feet]'] || 0;
      const diameterInch = +values[1]['properties[diameter-inches]'] || 0;
      const diameter = diameterFeet + (diameterInch / 12);
      return Math.ceil(diameter * Math.PI);
    } else if (this.chosenShape === 'square') {
      const widthFeet = +values[0]['properties[width-feet]'] || 0;
      const widthInch = +values[1]['properties[width-inches]'] || 0;
      const width = widthFeet + (widthInch / 12);
      return Math.ceil(width * 4);
    }
    return 0;
  }

  /**
   * Update price display with dimension and calculated price
   * Matches TypeScript updatePrice() line 600-636
   */
  updatePrice() {
    if (!this.priceDisplay) return;

    // If no area calculated, show price per sq ft
    // Matches TypeScript updatePrice() line 601-605
    if (!this.chosenArea && !this.chosenPerimeter) {
      const priceString = this.formatMoney(this.pricePerSqFt);
      this.priceDisplay.textContent = `${priceString} /sq ft.`;
      return;
    }

    // Calculate base price from area (pricePerSqFt is in cents, result is in cents)
    // Matches TypeScript updatePrice() line 607
    let price = this.chosenArea * this.pricePerSqFt;

    // Add finishing cost (perimeter-based) if selected
    // Matches TypeScript updatePrice() line 609-618 EXACTLY
    const finishingDirective = this.cylindoSelects.find((s) => s.optionName.toLowerCase() === "finishing");
    
    if (this.chosenPerimeter > 0) {
      const value = finishingDirective?.element.querySelector('input[type="radio"]:checked')?.value;
      if (value) {
        const variant = this.flowProducts.flatMap((p) => p.variants || []).find(v => v.id === +value);
        if (variant && variant.price) {
          price += this.chosenPerimeter * variant.price;
        }
      }
    }

    // Add rug pad cost (area-based) if selected
    // Matches TypeScript updatePrice() line 620-624 EXACTLY
    const rugPadDirective = this.cylindoSelects.find((s) => s.optionName.toLowerCase() === "matching rug pad");
    
    const rugPadValue = rugPadDirective?.element.querySelector('input[type="radio"]:checked')?.value;
    if (rugPadValue) {
      const variant = this.flowProducts.flatMap(p => p.variants || []).find(v => v.id === +rugPadValue);
      if (variant && variant.price) {
        price += (variant.price || 0) * this.chosenArea;
      }
    }
    

    // Format price
    const priceString = this.formatMoney(price);

    // Get dimension string for display (matches TypeScript updatePrice() line 627-630)
    let dimString = '';
    if (this.chosenShape) {
      const dimensionContainer = this.container.querySelector(
        `.custom-rug-option__dimension--${this.chosenShape}[size-fields="active"]`
      );
      if (dimensionContainer) {
        // Create values array like TypeScript does (line 628)
        const inputs = dimensionContainer.querySelectorAll('input[type="number"]');
        const values = Array.from(inputs).map(input => ({ [input.name]: input.value }));
        dimString = this.getDimensionStringFromValues(values, this.chosenShape);
      }
    }

    // Update price display: "5' x 5' - $1,575.00" (matches TypeScript line 630)
    if (dimString) {
      this.priceDisplay.textContent = `${dimString} - ${priceString}`;
    } else {
      this.priceDisplay.textContent = priceString;
    }
    
    // Store current price for button update
    this.currentPrice = price;
    
    // Update add to cart button text with price (matches TypeScript line 633-635)
    // Format: "Add to cart · $1,139.00" (matches live site)
    if (this.addToCartBtn && this.currentVariant?.available && this.chosenArea > 0) {
      this.addToCartBtn.innerHTML = `Add to cart &middot; ${priceString}`;
    } else if (this.addToCartBtn && this.currentVariant?.available) {
      // If no dimensions yet, just show "Add to cart"
      this.addToCartBtn.textContent = 'Add to cart';
    }
  }

  /**
   * Get variant from color value
   * Helper method to get variant data from CustomRugsProducts JSON
   */
  getVariantFromColorValue(colorValue) {
    try {
      const customRugsProductsScript = document.getElementById('CustomRugsProducts');
      if (!customRugsProductsScript) return null;
      
      const customRugsData = JSON.parse(customRugsProductsScript.textContent);
      const rugProductVariants = customRugsData.rug_product_variants || [];
      
      // Try to find variant by ID first
      let variant = rugProductVariants.find(v => v.id === parseInt(colorValue));
      
      // If not found by ID, try to find by option1 (color) match
      if (!variant) {
        const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
        if (colorSelector) {
          const checkedInput = colorSelector.element.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const label = checkedInput.closest('label');
            const labelText = label?.querySelector('.custom-rug-option__label')?.textContent.trim();
            
            // Find variant where option1 matches the label text
            variant = rugProductVariants.find(v => {
              return v.option1 && labelText && 
                     v.option1.toLowerCase() === labelText.toLowerCase();
            });
          }
        }
      }
      
      return variant || null;
    } catch (e) {
      console.warn('Error getting variant from color value:', e);
      return null;
    }
  }

  // Matches TypeScript validate() line 638-652
  validate() {
    if (!this.addToCartBtn) return;
    
    // Required options: color and shape must always be selected
    const requiredOpts = ['color', 'shape'];
    const requiredDirectives = this.cylindoSelects.filter(s => requiredOpts.includes(s.optionName.toLowerCase()));
    const requiredOptsValid = requiredDirectives.length === requiredOpts.length && 
                              requiredDirectives.every(({ element }) => !!element.querySelector('input[type="radio"]:checked'));

    // Optional options: finishing and rug pad (only validate if they exist and are enabled)
    const optionalOpts = ['finishing', 'matching rug pad'];
    const optionalDirectives = this.cylindoSelects.filter(s => {
      const name = s.optionName.toLowerCase();
      return optionalOpts.includes(name) && s.toggle && !s.toggle.disabled;
    });
    const optionalOptsValid = optionalDirectives.length === 0 || 
                              optionalDirectives.every(({ element }) => !!element.querySelector('input[type="radio"]:checked'));

    const sizeDirective = this.cylindoSelects.find((s) => s.optionName.toLowerCase() === "size");
    const sizeDisplayValue = sizeDirective?.toggle?.selectedLabel?.textContent?.trim() || '';
    const sizeValid = this.chosenArea > 0 && this.chosenPerimeter > 0 && sizeDisplayValue.length > 0;
    const hasValidSize = this.addToCartBtn.getAttribute('has-valid-size') === 'true';

    // Enable button if: has valid size AND required options selected AND optional options (if enabled) selected AND size valid AND variant available
    // This allows finishing/rug pad to be optional if they're not enabled
    if (hasValidSize && requiredOptsValid && optionalOptsValid && sizeValid && this.currentVariant?.available) {
      this.addToCartBtn.disabled = false;
    } else {
      this.addToCartBtn.disabled = true;
    }
  }

  getCurrentVariant() {
    // Get current variant from form or variant picker
    const form = this.container.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    
    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return null;
    
    // Try to get variant data from ProductJson or similar
    const productJson = document.getElementById('ProductJson');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        const variantId = parseInt(variantInput.value);
        return product.variants?.find(v => v.id === variantId) || null;
      } catch (e) {
        return null;
      }
    }
    
    return { available: true }; // Fallback
  }

  /**
   * Get variant from color value
   * Helper method to get variant data from CustomRugsProducts JSON
   */
  getVariantFromColorValue(colorValue) {
    try {
      const customRugsProductsScript = document.getElementById('CustomRugsProducts');
      if (!customRugsProductsScript) return null;
      
      const customRugsData = JSON.parse(customRugsProductsScript.textContent);
      const rugProductVariants = customRugsData.rug_product_variants || [];
      
      // Try to find variant by ID first
      let variant = rugProductVariants.find(v => v.id === parseInt(colorValue));
      
      // If not found by ID, try to find by option1 (color) match
      if (!variant) {
        const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
        if (colorSelector) {
          const checkedInput = colorSelector.element.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const label = checkedInput.closest('label');
            const labelText = label?.querySelector('.custom-rug-option__label')?.textContent.trim();
            
            // Find variant where option1 matches the label text
            variant = rugProductVariants.find(v => {
              return v.option1 && labelText && 
                     v.option1.toLowerCase() === labelText.toLowerCase();
            });
          }
        }
      }
      
      return variant || null;
    } catch (e) {
      console.warn('Error getting variant from color value:', e);
      return null;
    }
  }

  /**
   * Update URL with variant ID based on color selection
   * Matches product-gallery.js updateURL() behavior
   */
  updateURLFromColorSelection(colorValue) {
    try {
      // Get variant from CustomRugsProducts JSON
      const customRugsProductsScript = document.getElementById('CustomRugsProducts');
      if (!customRugsProductsScript) return;
      
      const customRugsData = JSON.parse(customRugsProductsScript.textContent);
      const rugProductVariants = customRugsData.rug_product_variants || [];
      
      // Find variant that matches the selected color
      // Color value is typically the variant ID or option value
      // Check if it's a variant ID or option value
      let variantId = null;
      
      // Try to find variant by ID first
      let variant = rugProductVariants.find(v => v.id === parseInt(colorValue));
      
      // If not found by ID, try to find by option1 (color) match
      if (!variant) {
        const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
        if (colorSelector) {
          const checkedInput = colorSelector.element.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const label = checkedInput.closest('label');
            const labelText = label?.querySelector('.custom-rug-option__label')?.textContent.trim();
            
            // Find variant where option1 matches the label text
            variant = rugProductVariants.find(v => {
              return v.option1 && labelText && 
                     v.option1.toLowerCase() === labelText.toLowerCase();
            });
            
            if (variant) {
              variantId = variant.id;
            }
          }
        }
      } else {
        variantId = variant.id;
      }
      
      // Update URL if variant found
      if (variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId.toString());
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    } catch (e) {
      console.warn('Error updating URL from color selection:', e);
    }
  }

  /**
   * Dispatch variant:change event to update gallery
   * Matches product-gallery.js variant:change event listener
   */
  dispatchVariantChangeEvent(colorValue) {
    try {
      // Get variant from CustomRugsProducts JSON
      const customRugsProductsScript = document.getElementById('CustomRugsProducts');
      if (!customRugsProductsScript) return;
      
      const customRugsData = JSON.parse(customRugsProductsScript.textContent);
      const rugProductVariants = customRugsData.rug_product_variants || [];
      
      // Find variant that matches the selected color
      let variantId = null;
      
      // Try to find variant by ID first
      let variant = rugProductVariants.find(v => v.id === parseInt(colorValue));
      
      // If not found by ID, try to find by option1 (color) match
      if (!variant) {
        const colorSelector = this.cylindoSelects.find(s => s.optionName.toLowerCase() === 'color');
        if (colorSelector) {
          const checkedInput = colorSelector.element.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            const label = checkedInput.closest('label');
            const labelText = label?.querySelector('.custom-rug-option__label')?.textContent.trim();
            
            // Find variant where option1 matches the label text
            variant = rugProductVariants.find(v => {
              return v.option1 && labelText && 
                     v.option1.toLowerCase() === labelText.toLowerCase();
            });
            
            if (variant) {
              variantId = variant.id;
            }
          }
        }
      } else {
        variantId = variant.id;
      }
      
      // Dispatch variant:change event if variant found (matches product-gallery.js line 655-660)
      if (variantId) {
        const variantChangeEvent = new CustomEvent('variant:change', {
          detail: {
            variantId: variantId,
            variant: { id: variantId }
          },
          bubbles: true
        });
        document.dispatchEvent(variantChangeEvent);
      }
    } catch (e) {
      console.warn('Error dispatching variant change event:', e);
    }
  }

  // Matches TypeScript updateFinishingPrices() line 554-561
  updateFinishingPrices() {
    const finishingDirective = this.cylindoSelects.find((s) => s.optionName.toLowerCase() === "finishing");
    if (!finishingDirective || this.chosenPerimeter <= 0) return;
    
    finishingDirective.element.querySelectorAll('label.custom-rug-option__finishing').forEach(label => {
      const pricePer = +label.getAttribute('price-per-ft') || 0;
      const price = this.chosenPerimeter * pricePer;
      const priceElement = label.querySelector('.custom-rug-option__finishing--price');
      if (priceElement) {
        priceElement.textContent = price === 0 ? 'FREE' : `${this.formatMoney(price)} (${this.formatMoney(pricePer)} /ft.)`;
      }
    });
  }

  // Matches TypeScript updateRugPadPrices() line 563-570 EXACTLY
  updateRugPadPrices() {
    const rugPadDirective = this.cylindoSelects.find((s) => s.optionName.toLowerCase() === "matching rug pad");
    if (!rugPadDirective || this.chosenArea <= 0) return;
    
    rugPadDirective.element.querySelectorAll('label.custom-rug-option__pad').forEach(label => {
      const pricePer = +label.getAttribute('price-per-sq-ft') || 0;
      const price = this.chosenArea * pricePer;
      const priceElement = label.querySelector('.custom-rug-option__pad--price');
      if (priceElement) {
        priceElement.textContent = price === 0 ? '' : this.formatMoney(price);
      }
    });
  }

  /**
   * Format money from cents to currency string (e.g., $1,575.00)
   * Matches TypeScript formatMoney() in ShopifyUtils.ts line 1-35
   */
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents.replace('.', '')) || 0;
    }
    
    // Convert cents to dollars with 2 decimal places
    const dollars = (cents / 100).toFixed(2);
    
    // Split into integer and decimal parts
    const parts = dollars.split('.');
    const dollarsPart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    const centsPart = parts[1] ? '.' + parts[1] : '';
    
    return '$' + dollarsPart + centsPart;
  }
  
  /**
   * Update upsell/sample tile when color variant changes
   * Matches TypeScript custom-rugs.directive.ts updateUpsell() line 507-519
   */
  updateUpsell(rugVariant) {
    try {
      // Get CustomRugsProducts data to find matching swatch variant
      const customRugsProductsScript = document.getElementById('CustomRugsProducts');
      if (!customRugsProductsScript) return;
      
      const customRugsData = JSON.parse(customRugsProductsScript.textContent);
      const swatchProduct = customRugsData.swatch_product;
      
      if (!swatchProduct || !swatchProduct.variants || !rugVariant) return;
      
      // Find swatch variant matching the rug variant options (matches TypeScript line 190)
      // TypeScript: const swatchVariant = this.swatchProduct?.variants.find(v => v.options.every((opt, i) => variant.options[i] === opt));
      const swatchVariant = swatchProduct.variants.find(v => {
        return v.options && rugVariant.options &&
               v.options.every((opt, i) => rugVariant.options[i] === opt);
      });
      
      if (!swatchVariant) return;
      
      // Get upsell container
      const upsell = this.container.querySelector('.custom-rug-upsell') || 
                     document.querySelector('.custom-rug-upsell');
      
      if (!upsell) return;
      
      // Update upsell image (matches TypeScript line 509-513)
      const upsellImg = upsell.querySelector('.upsellTile__img');
      if (upsellImg && swatchVariant.featured_image && swatchVariant.featured_image.src) {
        // Create image URL with 58x58 size for Purchase a Sample swatch
        let imgUrl = swatchVariant.featured_image.src;
        
        // Generate 58x58 image URL
        let img58Url = imgUrl;
        if (imgUrl.includes('cdn.shopify.com') || imgUrl.includes('/cdn/shop/')) {
          // Remove existing size parameters
          const cleanUrl = imgUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|3000x3000|master|\d+x\d+)+\./gi, '.');
          // Extract file extension
          const extMatch = cleanUrl.match(/\.(jpg|png|gif|jpeg|webp)(\?.*)?$/i);
          if (extMatch) {
            const ext = extMatch[0];
            // Add 58x58 size
            img58Url = cleanUrl.replace(ext, `_58x58${ext}`);
          }
        }
        
        // Update src and srcset directly (not data-src/data-srcset)
        upsellImg.src = img58Url;
        upsellImg.srcset = `${img58Url} 58w`;
        
        // Also update data attributes for lazyload consistency
        upsellImg.setAttribute('data-src', img58Url);
        upsellImg.setAttribute('data-srcset', `${img58Url} 58w`);
      }
      
      // Update upsell title (matches TypeScript line 514)
      const upsellTitle = upsell.querySelector('.upsellTile__productTitle');
      if (upsellTitle && swatchVariant.title) {
        upsellTitle.textContent = swatchVariant.title;
      }
      
      // Update upsell price (matches TypeScript line 515)
      const upsellPrice = upsell.querySelector('.price-value__current, .upsellTile__productValue .price-value__current');
      if (upsellPrice && swatchVariant.price) {
        upsellPrice.textContent = this.formatMoney(swatchVariant.price);
      }
      
      // Update upsell add-to-cart button with swatch variant ID (matches TypeScript line 516)
      const upsellCta = upsell.querySelector('.upsellTile__ctaButton, [add-to-cart]');
      if (upsellCta && swatchVariant.id) {
        upsellCta.setAttribute('add-to-cart', swatchVariant.id.toString());
        upsellCta.disabled = !swatchVariant.available;
      }
      
      // Update upsell select dropdown to match the variant
      const upsellSelect = upsell.querySelector('.upsellTile__optionsInput');
      if (upsellSelect && swatchVariant.id) {
        // Find the option matching this variant ID
        const matchingOption = upsellSelect.querySelector(`option[value="${swatchVariant.id}"]`);
        if (matchingOption) {
          upsellSelect.value = swatchVariant.id.toString();
        }
      }
      
      // Toggle upsell visibility based on rug variant availability (matches TypeScript line 517)
      if (this.currentVariant) {
        const shouldHide = !this.currentVariant.available;
        if (shouldHide) {
          upsell.classList.add('custom-rug-upsell--hidden');
        } else {
          upsell.classList.remove('custom-rug-upsell--hidden');
        }
      }
      
    } catch (e) {
      console.warn('[Custom Rugs] Error updating upsell:', e);
    }
  }
}

// Initialize quantity selectors (for +/- buttons)
class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.decreaseBtn = container.querySelector('[quantity-selector-decrease]');
    this.increaseBtn = container.querySelector('[quantity-selector-increase]');
    this.valueInput = container.querySelector('[quantity-selector-value]');
    
    if (!this.decreaseBtn || !this.increaseBtn || !this.valueInput) {
      return;
    }
    
    this.init();
  }

  init() {
    this.decreaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.decrease();
    });

    this.increaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.increase();
    });

    this.valueInput.addEventListener('change', () => {
      this.updateButtonStates();
    });

    this.updateButtonStates();
  }

  decrease() {
    const currentValue = parseInt(this.valueInput.value) || 0;
    const min = parseInt(this.valueInput.min) || 0;
    const newValue = Math.max(min, currentValue - 1);
    this.valueInput.value = newValue;
    this.valueInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateButtonStates();
  }

  increase() {
    const currentValue = parseInt(this.valueInput.value) || 0;
    const max = parseInt(this.valueInput.max) || Infinity;
    const newValue = Math.min(max, currentValue + 1);
    this.valueInput.value = newValue;
    this.valueInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateButtonStates();
  }

  updateButtonStates() {
    const value = parseInt(this.valueInput.value) || 0;
    const min = parseInt(this.valueInput.min) || 0;
    const max = parseInt(this.valueInput.max) || Infinity;

    if (this.decreaseBtn) {
      this.decreaseBtn.disabled = value <= min;
    }
    if (this.increaseBtn) {
      this.increaseBtn.disabled = value >= max;
    }
  }
}

// Initialize custom rugs handlers
// Must run after cylindo-variant-toggle.js initializes
function initializeCustomRugs() {
  const containers = document.querySelectorAll('[custom-rugs]');
  containers.forEach(container => {
    // Don't re-initialize
    if (container.customRugsHandler) {
      return;
    }
    
    // Check if cylindo toggles are initialized
    const hasToggles = container.querySelectorAll('[cylindo-variant-selector]').length > 0;
    if (!hasToggles) {
      // Retry if toggles not ready yet
      setTimeout(initializeCustomRugs, 100);
      return;
    }
    
    // Wait a bit more to ensure all toggles are initialized
    setTimeout(() => {
      container.customRugsHandler = new CustomRugsHandler(container);
    }, 150);
  });
}

// Initialize quantity selectors
function initializeQuantitySelectors() {
  const containers = document.querySelectorAll('[quantity-selector]');
  containers.forEach(container => {
    if (container.quantitySelectorInstance) {
      return;
    }
    container.quantitySelectorInstance = new QuantitySelector(container);
  });
}

// Initialize on DOM ready
// Note: custom-rugs-handler must load AFTER cylindo-variant-toggle.js
function initCustomRugsHandler() {
  initializeQuantitySelectors();
  // Wait for cylindo-variant-toggle.js to initialize first
  setTimeout(initializeCustomRugs, 300);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomRugsHandler);
} else {
  initCustomRugsHandler();
}

// Re-initialize on section load
document.addEventListener('shopify:section:load', () => {
  initializeQuantitySelectors();
  setTimeout(initializeCustomRugs, 200);
});

// Export for external use
window.CustomRugsHandler = CustomRugsHandler;
window.QuantitySelector = QuantitySelector;
window.initializeCustomRugs = initializeCustomRugs;
window.initializeQuantitySelectors = initializeQuantitySelectors;

